// Invoices — every open invoice from QuickBooks, overdue badges, footer total.
import React from "react";
import { C, font } from "../../../lib/styles.js";
import { useQbo } from "../lib/useQbo.js";
import {
  Badge, ErrorPanel, Loading, RefreshButton, daysUntil, money, panel, td, tdNum, th,
} from "./common.jsx";

const dueBadge = (inv) => {
  if (inv.overdueDays > 0) return <Badge tone="over">{inv.overdueDays} days over</Badge>;
  // Local-calendar-day math — UTC-midnight parsing said "due in 0 days" for
  // invoices actually due tomorrow (codex review).
  const days = daysUntil(inv.dueDate);
  if (days !== null && days >= 0 && days < 7) {
    return <Badge tone="soon">due {days === 0 ? "today" : `in ${days} day${days === 1 ? "" : "s"}`}</Badge>;
  }
  return <Badge tone="ok">open</Badge>;
};

export default function Invoices({ ctx }) {
  const { data, loading, error, refresh } = useQbo(ctx, "list_open_invoices");
  if (loading && !data) return <Loading label="Loading invoices…" />;
  if (error) return <ErrorPanel error={error} onRetry={refresh} />;

  const invoices = data?.invoices ?? [];
  const totalOpen = invoices.reduce((s, i) => s + (i.balance ?? 0), 0);

  return (
    <div style={{ fontFamily: font }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, color: C.muted }}>
          {invoices.length} open invoice(s){data?.hasMore ? " — more exist in QuickBooks" : ""}
        </span>
        <RefreshButton onClick={refresh} />
      </div>
      <div style={{ ...panel, padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>#</th><th style={th}>Customer</th><th style={th}>Date</th>
              <th style={th}>Due</th><th style={{ ...th, textAlign: "right" }}>Total</th>
              <th style={{ ...th, textAlign: "right" }}>Balance</th><th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id}>
                <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{i.docNumber ?? "—"}</td>
                <td style={td}>{i.customer ?? "—"}</td>
                <td style={td}>{i.txnDate ?? "—"}</td>
                <td style={td}>{i.dueDate ?? "—"}</td>
                <td style={tdNum}>{money(i.total)}</td>
                <td style={tdNum}>{money(i.balance)}</td>
                <td style={td}>{dueBadge(i)}</td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td style={{ ...td, color: C.muted, textAlign: "center" }} colSpan={7}>No open invoices.</td></tr>
            )}
            {invoices.length > 0 && (
              <tr>
                <td style={{ border: "none" }} colSpan={4} />
                <td style={{ ...tdNum, border: "none", fontWeight: 500 }}>{data?.hasMore ? "Total shown" : "Total open"}</td>
                <td style={{ ...tdNum, border: "none", fontWeight: 500 }}>{money(totalOpen)}</td>
                <td style={{ border: "none" }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
